import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import httpClient from '../../../shared/lib/httpClient';
import { ENDPOINTS } from '../../../shared/services/api/endpoints';
import { useUserStore } from '../../auth/store/userStore';
import { snapshotShippingAddress, restoreShippingAddress } from '../../../shared/utils/sameAsBillingShipping';
import { toRegionOption, findRegionOption } from '../../../shared/hooks/useCountryRegionOptions';

/**
 * Backs ShippingAddressSection in the profile modal. Country is NOT selected here — it's shared
 * with BillingAddressSection/useBillingAddress (a HaloPSA Site has only one country/region pair),
 * so this hook only loads the region list for whichever country is already selected on the shared
 * `country` form field, and tracks the shipping-only `shippingRegion` field independently.
 *
 * This does mean a second `GET_REGIONS_BY_COUNTRY` call for the same country id that
 * useBillingAddress already fetched — a deliberate simplification to avoid changing
 * BillingAddressSection's API (it owns its region list privately, not via props) for what is a
 * profile-modal-only, low-frequency form.
 *
 * Also owns the "Same as billing address" checkbox — mirrors the same pattern used by
 * EditBillingDetailsDialog and useCheckoutAddressForm: shipping is live-synced from billing while
 * checked; unchecking brings back the shipping address held before ticking, or empty fields (see
 * shared/utils/sameAsBillingShipping); and it is initialized from the flag HaloPSA actually saved last time (`sameAsBilling`, see
 * ShippingAddressDto) rather than always defaulting to unchecked.
 */
export function useShippingAddress({ setValue, watch, getValues, isSubmitted = false }) {
  // Reads a field without subscribing to it. watch(name) called from an event handler would add the
  // field to the form's watched set and re-render the whole profile form on every keystroke;
  // getValues doesn't. Falls back to watch for callers that don't pass getValues.
  const readValue = useCallback((name) => (getValues ? getValues(name) : watch(name)), [getValues, watch]);
  const { profile } = useUserStore();
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [availableRegions, setAvailableRegions] = useState([]);

  const selectedCountry = watch('country');
  const selectedShippingRegion = watch('shippingRegion');
  const shippingSameAsBilling = watch('shippingSameAsBilling');
  const billingAddress1 = watch('address1');
  const billingAddress2 = watch('address2');
  const billingAddress3 = watch('address3');
  const billingPostalCode = watch('postalCode');
  const billingRegion = watch('region');

  const hasHydratedSameAsBilling = useRef(false);
  // The region list the saved State was last restored against — the restore runs once per list,
  // so picking "No region/state" (an empty value) isn't immediately overwritten by the saved one.
  const restoredForRegionsRef = useRef(null);

  // Passed to every programmatic setValue below — setValue never re-runs validation on its own,
  // so after a failed save a shipping error stayed on screen even once billing's values were
  // copied in. Re-validates only after a first save attempt, so nothing is flagged before then.
  const revalidate = useMemo(() => ({ shouldValidate: isSubmitted }), [isSubmitted]);

  useEffect(() => {
    if (!selectedCountry) {
      setAvailableRegions([]);
      return undefined;
    }

    let cancelled = false;
    const loadRegions = async () => {
      try {
        setLoadingRegions(true);
        const countryId = typeof selectedCountry === 'string' ? Number.parseInt(selectedCountry, 10) : selectedCountry;
        const response = await httpClient.get(ENDPOINTS.IDENTITY.GET_REGIONS_BY_COUNTRY(countryId));
        const regionsData = response.data?.data?.regions || [];
        if (!cancelled) setAvailableRegions(regionsData.map(toRegionOption));
      } catch {
        // Deliberately silent: the shipping State/Region is optional, and the backend returns 400
        // for a country with no regions on file — degrading to an empty, disabled dropdown is the
        // intended outcome. BillingAddressSection owns the country selection for this modal and
        // reports any country-load failure, so a second toast here would only duplicate it.
        if (!cancelled) setAvailableRegions([]);
      } finally {
        if (!cancelled) setLoadingRegions(false);
      }
    };

    loadRegions();
    return () => {
      cancelled = true;
    };
  }, [selectedCountry]);

  // Resolve the saved shipping State (free text on HaloPSA's side: a short code, or the full name on
  // older addresses) to a region option once the list has loaded — there is no id to key off.
  useEffect(() => {
    if (availableRegions.length === 0 || restoredForRegionsRef.current === availableRegions) return;
    restoredForRegionsRef.current = availableRegions;
    if (selectedShippingRegion) return;
    const savedState = profile?.shippingAddress?.state;
    if (!savedState) return;
    const found = findRegionOption(availableRegions, savedState);
    if (found) {
      setValue('shippingRegion', found.value);
    }
  }, [availableRegions, profile, selectedShippingRegion, setValue]);

  // Initialize the checkbox from the flag HaloPSA actually saved last time — runs once per profile load (guarded so it doesn't
  // re-fire and clobber an in-progress edit on every unrelated profile change).
  useEffect(() => {
    if (!profile?.shippingAddress || hasHydratedSameAsBilling.current) return;
    hasHydratedSameAsBilling.current = true;
    setValue('shippingSameAsBilling', profile.shippingAddress.sameAsBilling === true);
  }, [profile, setValue]);

  // Live-sync shipping's line1/line2/line3/postcode/region from billing while checked — billing
  // has its own Address Line 3 (invoice_address.line3) now, so it syncs the same as the other
  // fields. ShippingAddressSection hides the shipping fields while checked (as HaloCRM and the
  // checkout page do); they stay registered, so this copy is still what gets validated and saved.
  useEffect(() => {
    if (!shippingSameAsBilling) return;
    setValue('shippingAddress1', billingAddress1, revalidate);
    setValue('shippingAddress2', billingAddress2, revalidate);
    setValue('shippingAddress3', billingAddress3, revalidate);
    setValue('shippingPostalCode', billingPostalCode, revalidate);
    setValue('shippingRegion', billingRegion, revalidate);
  }, [shippingSameAsBilling, billingAddress1, billingAddress2, billingAddress3, billingPostalCode, billingRegion, selectedShippingRegion, setValue, revalidate]);

  const handleShippingRegionChange = useCallback((value) => {
    setValue('shippingRegion', value, revalidate);
  }, [setValue, revalidate]);

  // The shipping address to bring back on untick — see shared/utils/sameAsBillingShipping. Starts
  // empty, so a form that loads already ticked comes back with empty fields.
  const shippingBeforeTickRef = useRef(null);

  // Forget it whenever the saved shipping address changes (e.g. a save re-syncs the profile), so an
  // address from before that save never comes back — as the Edit Billing dialog clears it on open.
  useEffect(() => {
    shippingBeforeTickRef.current = null;
  }, [profile?.shippingAddress]);

  const handleShippingSameAsBillingChange = useCallback((checked) => {
    if (checked === true) {
      // Remember the user's own shipping address before the sync effect above copies billing in.
      shippingBeforeTickRef.current = snapshotShippingAddress(readValue);
      setValue('shippingSameAsBilling', true, revalidate);
      return;
    }
    // Flag first, so the sync effect stops copying; then bring back the remembered address (or empty).
    setValue('shippingSameAsBilling', false, revalidate);
    restoreShippingAddress(setValue, shippingBeforeTickRef.current, readValue('country'));
  }, [setValue, readValue, revalidate]);

  return {
    availableRegions,
    loadingRegions,
    handleShippingRegionChange,
    shippingSameAsBilling: shippingSameAsBilling === true,
    handleShippingSameAsBillingChange,
  };
}
